import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Btn, Input, Select, Badge, Modal } from "@/components/UI";
import { MessageSquare, Send, Bot, Sparkles, User, Clock, AlertCircle, Search, ExternalLink, Package, Factory, Truck, Settings as SettingsIcon, RefreshCw } from "lucide-react";

/**
 * Müşteri Hizmetleri — İki panelli sohbet ekranı.
 * Sol: cevap bekleyen / tüm müşteri konuşmaları
 * Sağ: aktif sohbet + AI cevap önerileri + gönderme
 */
export default function CustomerServicePage() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ q: "", status: "all", priority: "all" });
  const [suggestions, setSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/customer-service/conversations"); setConversations(r.conversations || []); } catch (e) {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  const loadDetail = useCallback(async (customerId) => {
    setSuggestions(null);
    try { const r = await api.get(`/customer-service/conversations/${customerId}`); setDetail(r); await load(); }
    catch (e) { alert(e.response?.data?.detail || "Yüklenemedi"); }
  }, [load]);

  const filtered = useMemo(() => {
    let arr = [...conversations];
    if (filter.q.trim()) arr = arr.filter(c => (c.customer_name || "").toLowerCase().includes(filter.q.toLowerCase()));
    if (filter.status === "waiting") arr = arr.filter(c => c.waiting_for_reply);
    else if (filter.status === "replied") arr = arr.filter(c => !c.waiting_for_reply);
    if (filter.priority !== "all") arr = arr.filter(c => (c.priority || "normal") === filter.priority);
    return arr;
  }, [conversations, filter]);

  const send = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true);
    try {
      await api.post("/customer-service/reply", { customer_id: selected.customer_id, text: reply });
      setReply(""); setSuggestions(null);
      await loadDetail(selected.customer_id);
    } catch (e) { alert(e.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  const askAI = async () => {
    if (!detail) return;
    const lastInbound = [...(detail.messages || [])].reverse().find(m => m.direction === "INBOUND");
    if (!lastInbound) { alert("Cevap bekleyen mesaj bulunamadı"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/customer-service/ai-suggest", { customer_id: selected.customer_id, message_id: lastInbound.id });
      setSuggestions(r);
    } catch (e) { alert(e.response?.data?.detail || "AI önerisi alınamadı"); }
    finally { setAiLoading(false); }
  };

  const selectSuggestion = (text) => { setReply(text); };
  const formatTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };
  const waitLabel = (m) => {
    if (m == null) return null;
    if (m < 60) return `${m} dk`;
    if (m < 1440) return `${Math.floor(m/60)} sa`;
    return `${Math.floor(m/1440)} gün`;
  };

  return (
    <div>
      <PageHeader title="Müşteri Hizmetleri" description="Telegram üzerinden gelen müşteri sorularını AI destekli olarak yanıtlayın" actions={<>
        <Btn variant="secondary" size="sm" onClick={() => setAdminOpen(true)} data-testid="cs-admin-settings"><SettingsIcon size={12}/> Bildirim Ayarları</Btn>
        <Btn size="sm" variant="secondary" onClick={load}><RefreshCw size={12}/> Yenile</Btn>
      </>}/>

      <div className="grid grid-cols-12 gap-4" style={{ minHeight: "70vh" }}>
        {/* Sol panel — konuşma listesi */}
        <Card className="col-span-4 p-3 flex flex-col" style={{ maxHeight: "78vh" }}>
          <div className="space-y-2 mb-3">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
              <input value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} placeholder="Müşteri ara..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded" data-testid="cs-search"/>
            </div>
            <div className="flex gap-1">
              <Select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} data-testid="cs-status-filter">
                <option value="all">Tümü</option><option value="waiting">Cevap Bekliyor</option><option value="replied">Cevaplandı</option>
              </Select>
              <Select value={filter.priority} onChange={e => setFilter({ ...filter, priority: e.target.value })}>
                <option value="all">Tüm Öncelikler</option><option value="normal">Normal</option><option value="important">Önemli</option><option value="urgent">Acil</option>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1" data-testid="cs-conversation-list">
            {filtered.length === 0 && <div className="text-center text-slate-400 text-xs py-8">Konuşma yok.</div>}
            {filtered.map(c => (
              <button key={c.customer_id} onClick={() => { setSelected(c); loadDetail(c.customer_id); }}
                className={`w-full text-left p-2 rounded border transition ${selected?.customer_id === c.customer_id ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`cs-row-${c.customer_id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm text-slate-800 truncate flex-1">{c.customer_name}</div>
                  {c.waiting_for_reply && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full" data-testid={`cs-waiting-${c.customer_id}`}><AlertCircle size={9}/> Cevap Bekliyor</span>}
                </div>
                <div className="text-[11px] text-slate-500 truncate italic">{c.last_direction === "OUTBOUND" ? "→ " : ""}{c.last_message_type === "voice" ? <span className="text-violet-600">🎙 </span> : null}{c.last_message || "(mesaj yok)"}</div>
                <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400">
                  <span>{formatTime(c.last_at)}</span>
                  <div className="flex items-center gap-1">
                    {c.priority && c.priority !== "normal" && <span className={`px-1 py-0.5 rounded text-white font-semibold ${c.priority === "urgent" ? "bg-red-500" : "bg-amber-500"}`}>{c.priority === "urgent" ? "ACİL" : "ÖNEMLİ"}</span>}
                    {c.waiting_minutes != null && <span className="text-orange-600 font-mono"><Clock size={9} className="inline"/> {waitLabel(c.waiting_minutes)}</span>}
                    {c.unread > 0 && <span className="bg-red-500 text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Sağ panel — aktif sohbet */}
        <Card className="col-span-8 p-0 flex flex-col" style={{ maxHeight: "78vh" }}>
          {!detail && (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-30"/>
                <div>Konuşma seçin</div>
              </div>
            </div>
          )}
          {detail && (
            <>
              {/* Header */}
              <div className="p-3 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-800">{detail.customer?.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Chat ID: {detail.customer?.telegram_chat_id}</div>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-slate-600"><Package size={11} className="inline text-indigo-500"/> {detail.context?.open_orders || 0} açık sipariş</span>
                    <span className="text-slate-600"><Factory size={11} className="inline text-orange-500"/> {detail.context?.active_productions || 0} aktif üretim</span>
                    <span className="text-slate-600"><Truck size={11} className="inline text-emerald-500"/> {detail.context?.pending_shipments || 0} bekleyen sevkiyat</span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/30" data-testid="cs-messages">
                {(detail.messages || []).map(m => {
                  const isIn = m.direction === "INBOUND";
                  const isSys = m.direction === "SYSTEM" || m.message_type === "system";
                  const isVoice = m.message_type === "voice";
                  const vdur = m.voice_meta?.duration;
                  return (
                    <div key={m.id} className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-sm ${isSys ? "bg-slate-100 text-slate-600 italic text-xs" : isIn ? "bg-white border border-slate-200" : "bg-indigo-600 text-white"}`}
                           data-testid={`cs-msg-${m.id}`}>
                        {isVoice && (
                          <div className={`flex items-center gap-1 mb-1 text-[10px] font-semibold uppercase tracking-wider ${isIn ? "text-violet-600" : "text-violet-200"}`} data-testid={`cs-voice-${m.id}`}>
                            🎙 Sesli Mesaj{vdur ? ` · ${vdur}s` : ""} · <span className="normal-case font-normal opacity-80">Whisper ile yazıya çevrildi</span>
                          </div>
                        )}
                        <div className="whitespace-pre-wrap">{m.message_text}</div>
                        <div className={`text-[9px] mt-1 ${isIn ? "text-slate-400" : "text-indigo-200"}`}>
                          {formatTime(m.created_at)}
                          {m.replied_by_name && ` · ${m.replied_by_name}`}
                          {m.status === "WAITING_FOR_REPLY" && <span className="ml-2 text-red-500 font-bold">● cevap bekliyor</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* AI Suggestions */}
              {suggestions && (
                <div className="border-t border-slate-200 p-3 bg-gradient-to-r from-violet-50 to-indigo-50" data-testid="cs-suggestions">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700 flex items-center gap-1"><Sparkles size={11}/> AI Cevap Önerileri</div>
                    <div className="flex gap-1 text-[10px]">
                      <Badge level="attention">{suggestions.ai_category || "genel"}</Badge>
                      <Badge level={suggestions.ai_priority === "urgent" ? "critical" : suggestions.ai_priority === "important" ? "attention" : "inactive"}>{suggestions.ai_priority || "normal"}</Badge>
                      {suggestions.needs_human && <Badge level="missing">İnsan Desteği Gerekiyor</Badge>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {(suggestions.suggestions || []).map((s, i) => (
                      <button key={i} onClick={() => selectSuggestion(s)} className="w-full text-left text-xs p-2 rounded bg-white border border-violet-200 hover:border-violet-400 hover:bg-violet-50 transition" data-testid={`cs-sug-${i}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  {(suggestions.used_sources || []).length > 0 && (
                    <div className="mt-2 text-[9px] text-slate-500">
                      <b>Kullanılan Kaynaklar (sadece size görünür):</b> {suggestions.used_sources.join(", ")}
                    </div>
                  )}
                </div>
              )}

              {/* Reply composer */}
              <div className="border-t border-slate-200 p-3">
                <div className="flex gap-2 mb-2">
                  <Btn size="sm" variant="secondary" onClick={askAI} disabled={aiLoading} data-testid="cs-ask-ai">
                    <Bot size={12}/> {aiLoading ? "AI Düşünüyor..." : "AI Cevap Önerileri Al"}
                  </Btn>
                  {suggestions && <Btn size="sm" variant="secondary" onClick={() => setSuggestions(null)}>Önerileri Kapat</Btn>}
                </div>
                <div className="flex gap-2">
                  <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Mesajınızı yazın... veya önerilerden birini seçin"
                    rows={2}
                    onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded resize-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                    data-testid="cs-reply-input"/>
                  <Btn onClick={send} disabled={busy || !reply.trim()} data-testid="cs-send">
                    <Send size={14}/> {busy ? "..." : "Gönder"}
                  </Btn>
                </div>
                <div className="text-[9px] text-slate-400 mt-1">Ctrl+Enter ile gönder · AI müşteriye hiçbir zaman otomatik cevap vermez</div>
              </div>
            </>
          )}
        </Card>
      </div>

      {adminOpen && <AdminSettingsModal onClose={() => setAdminOpen(false)}/>}
    </div>
  );
}

function AdminSettingsModal({ onClose }) {
  const [s, setS] = useState({ admin_chat_ids: [], notify_on_customer_message: true });
  const [newId, setNewId] = useState("");
  useEffect(() => { api.get("/customer-service/admin-settings").then(setS).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/customer-service/admin-settings", s); onClose(); }
    catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Yönetici Telegram Bildirimleri" size="md">
      <div className="space-y-3">
        <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">Buraya eklenen Chat ID'lere yeni müşteri mesajı geldiğinde otomatik bildirim gönderilir. Kendi Telegram Chat ID'nizi öğrenmek için bota <b>/whoami</b> yazın.</div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.notify_on_customer_message} onChange={e => setS({ ...s, notify_on_customer_message: e.target.checked })} data-testid="cs-notify-toggle"/>
          Yeni müşteri mesajlarında Telegram bildirimi gönder
        </label>
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Yönetici Chat ID'leri:</div>
          <div className="space-y-1">
            {(s.admin_chat_ids || []).map((cid, i) => (
              <div key={i} className="flex gap-1 items-center text-xs">
                <div className="flex-1 font-mono bg-slate-100 px-2 py-1 rounded">{cid}</div>
                <button onClick={() => setS({ ...s, admin_chat_ids: s.admin_chat_ids.filter((_, j) => j !== i) })} className="text-rose-500 text-xs" data-testid={`delete-admin-chat-${i}`}>Sil</button>
              </div>
            ))}
          </div>
          <div className="flex gap-1 mt-2">
            <input value={newId} onChange={e => setNewId(e.target.value)} placeholder="Chat ID (örn: 123456789)" className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded font-mono" data-testid="cs-admin-chat-input"/>
            <Btn size="sm" onClick={() => { if (newId.trim()) { setS({ ...s, admin_chat_ids: [...(s.admin_chat_ids || []), newId.trim()] }); setNewId(""); } }} data-testid="cs-admin-chat-add">Ekle</Btn>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="cs-admin-save">Kaydet</Btn>
        </div>
      </div>
    </Modal>
  );
}
